import {
  BookOpenTextIcon,
  Button,
  ChevronDownIcon,
  CoachMark,
  CompassIcon,
  CopyIcon,
  DropdownMenu,
  Flex,
  IconButton,
  LayoutGridIcon,
  MessageCircleQuestionMarkIcon,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  AGENT_INSTRUCTIONS,
  AGENT_INSTRUCTIONS_HINT,
} from '../../constants/agentInstructions'
import { copyToClipboard } from '../../helpers/copyToken'

import type { JSX } from 'react'

const DEVELOPER_PORTAL_URL = 'https://developers.buffer.com/'
const API_EXPLORER_URL = 'https://developers.buffer.com/explorer.html'
const GUIDES_URL = 'https://developers.buffer.com/guides/getting-started.html'
const INTEGRATIONS_URL =
  'https://developers.buffer.com/guides/integrations.html'
const HELP_CENTER_URL =
  'https://support.buffer.com/articles/using-buffers-api-GtIYIQilz5'

interface PageActionsProps {
  showCoachMark?: boolean
  onCoachMarkDismiss?: () => void
}

const PageActions = ({
  showCoachMark,
  onCoachMarkDismiss,
}: PageActionsProps): JSX.Element => {
  const apiDocsActions = (
    <Button.Group variant="secondary">
      <Button
        as="a"
        href={DEVELOPER_PORTAL_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <BookOpenTextIcon />
        API Docs
      </Button>
      <DropdownMenu modal={false}>
        <DropdownMenu.Trigger>
          <IconButton label="More developer resources">
            <ChevronDownIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          <DropdownMenu.Item asChild>
            <a
              href={API_EXPLORER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CompassIcon />
              Explore the API
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <a href={GUIDES_URL} target="_blank" rel="noopener noreferrer">
              <BookOpenTextIcon />
              Guides
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <a
              href={INTEGRATIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LayoutGridIcon />
              Integrations
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <Tooltip
            content={AGENT_INSTRUCTIONS_HINT}
            side="bottom"
            sideOffset={16}
          >
            <DropdownMenu.Item
              onSelect={(): void => {
                void copyToClipboard(AGENT_INSTRUCTIONS, {
                  successMessage: 'Agent instructions copied',
                })
              }}
            >
              <CopyIcon />
              Copy agent instructions
            </DropdownMenu.Item>
          </Tooltip>
        </DropdownMenu.Content>
      </DropdownMenu>
    </Button.Group>
  )

  const helpCenterButton = (
    <Button
      variant="secondary"
      as="a"
      href={HELP_CENTER_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <MessageCircleQuestionMarkIcon />
      Help Center
    </Button>
  )

  if (!showCoachMark) {
    return (
      <Flex gap="xs">
        {apiDocsActions}
        {helpCenterButton}
      </Flex>
    )
  }

  return (
    <Flex gap="xs">
      <CoachMark open={showCoachMark} onDismiss={onCoachMarkDismiss}>
        <CoachMark.Overlay>
          {apiDocsActions}
          <CoachMark.Spotlight />
        </CoachMark.Overlay>
        <CoachMark.Content side="bottom" align="end">
          <CoachMark.Title>Refer to the API docs here</CoachMark.Title>
          <CoachMark.Description>
            Get examples and full API reference any time by clicking this
            button.
          </CoachMark.Description>
          <CoachMark.Footer>
            <CoachMark.Dismiss>
              <Button variant="success" size="small">
                Got It
              </Button>
            </CoachMark.Dismiss>
          </CoachMark.Footer>
        </CoachMark.Content>
      </CoachMark>
      {helpCenterButton}
    </Flex>
  )
}

export { PageActions }
